import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../API';
import Tooltip from '../../../components/Tooltip';
import {
    QuestionConfigSection,
    BulkImportModal,
    normalizeSurveysConfig,
    createEmptySurvey,
    getSurveyTypeLabel,
    createDefaultQuestion,
    SURVEY_TYPE_ORDER,
    SURVEY_TYPES,
} from '../questions';
import { DRAFT_NAV_BLOCKED } from '../projectSettingsDraft';
import { moveIndex } from '../hub/orderedListUtils';
import formStyles from '../ProjectEditForm.module.css';
import styles from './SurveysSettingsPanel.module.css';

const TYPE_BLURBS = {
    [SURVEY_TYPES.PRE_TEST]:
        'Required before participants can open the annotation viewer.',
    [SURVEY_TYPES.GENERAL]:
        'Available anytime from the project homepage.',
    [SURVEY_TYPES.POST_TEST]:
        'Available after a participant has completed all images.',
};

function SurveysSettingsPanel({
    surveysConfig,
    setSurveysConfig,
    settingsLocked = false,
    folderId,
    hasUnsavedSettings = false,
}) {
    const navigate = useNavigate();
    const surveys = useMemo(
        () => normalizeSurveysConfig(surveysConfig),
        [surveysConfig],
    );

    const [confirmDisableType, setConfirmDisableType] = useState(null);
    const [confirmClearType, setConfirmClearType] = useState(null);
    const [clearBusyType, setClearBusyType] = useState(null);
    const [bulkQuestionKey, setBulkQuestionKey] = useState(null);

    function setSurveys(next) {
        setSurveysConfig(normalizeSurveysConfig(next));
    }

    function patchSurvey(type, patch) {
        setSurveys(surveys.map((s) => (s.type === type ? { ...s, ...patch } : s)));
    }

    function enableSurvey(type) {
        if (surveys.some((s) => s.type === type)) return;
        setSurveys([...surveys, createEmptySurvey(type)]);
        setConfirmDisableType(null);
    }

    function disableSurvey(type) {
        if (confirmDisableType !== type) {
            setConfirmDisableType(type);
            return;
        }
        setSurveys(surveys.filter((s) => s.type !== type));
        setConfirmDisableType(null);
    }

    function updateQuestion(surveyType, qIndex, patch) {
        const survey = surveys.find((s) => s.type === surveyType);
        if (!survey) return;
        const questions = survey.questions.map((q, i) => (
            i === qIndex ? { ...q, ...patch } : q
        ));
        patchSurvey(surveyType, { questions });
    }

    function addQuestion(surveyType) {
        const survey = surveys.find((s) => s.type === surveyType);
        if (!survey) return;
        const existingIds = survey.questions.map((q) => q.id);
        patchSurvey(surveyType, {
            questions: [...survey.questions, createDefaultQuestion(existingIds)],
        });
    }

    function removeQuestion(surveyType, qIndex) {
        const survey = surveys.find((s) => s.type === surveyType);
        if (!survey || survey.questions.length <= 1) return;
        patchSurvey(surveyType, {
            questions: survey.questions.filter((_, i) => i !== qIndex),
        });
    }

    function moveQuestion(surveyType, from, to) {
        const survey = surveys.find((s) => s.type === surveyType);
        if (!survey) return;
        const questions = moveIndex(survey.questions, from, to);
        if (questions) patchSurvey(surveyType, { questions });
    }

    function previewSurvey(type) {
        if (!folderId || hasUnsavedSettings) return;
        navigate(`/projects/${folderId}/survey/${type}`);
    }

    async function clearMyResponse(type) {
        if (!folderId) return;
        if (confirmClearType !== type) {
            setConfirmClearType(type);
            return;
        }
        setConfirmClearType(null);
        setClearBusyType(type);
        try {
            const r = await api.delete(
                `projects/${folderId}/surveys/${type}/response`,
                {},
            );
            if (r.status === 200 || r.status === 404) {
                // 404 = already empty; treat as cleared
                return;
            }
            window.alert(r.data?.message || 'Could not clear survey response.');
        } catch (e) {
            window.alert(e.message || 'Could not clear survey response.');
        } finally {
            setClearBusyType(null);
        }
    }

    const bulkParts = bulkQuestionKey ? String(bulkQuestionKey).split('\0') : null;
    const bulkSurveyType = bulkParts?.[0] || null;
    const bulkQIndex = bulkParts ? Number(bulkParts[1]) : -1;
    const previewBlocked = !folderId || hasUnsavedSettings;

    return (
        <>
            <div className={formStyles.panel}>
                {settingsLocked && (
                    <p className={formStyles.panelLockBanner}>
                        Published: return to draft to edit this section.
                    </p>
                )}
                <p className={styles.intro}>
                    Optional questionnaires for participants. Enable any combination of
                    Pre-test, General, and Post-test (at most one of each). Preview opens
                    the participant survey page (save settings first).
                </p>

                <div className={styles.surveyList}>
                    {SURVEY_TYPE_ORDER.map((type) => {
                        const survey = surveys.find((s) => s.type === type);
                        const enabled = Boolean(survey);
                        return (
                            <div
                                key={type}
                                className={[
                                    styles.surveyCard,
                                    enabled ? styles.surveyCardEnabled : styles.surveyCardDisabled,
                                ].join(' ')}
                            >
                                <div className={styles.surveyHeader}>
                                    <span className={styles.typeBadge}>
                                        {getSurveyTypeLabel(type)}
                                    </span>
                                    <p className={styles.typeBlurb}>
                                        {TYPE_BLURBS[type]}
                                    </p>
                                    <div className={styles.headerActions}>
                                        {enabled && folderId ? (
                                            <>
                                                {previewBlocked ? (
                                                    <Tooltip
                                                        text={
                                                            hasUnsavedSettings
                                                                ? DRAFT_NAV_BLOCKED
                                                                : 'Save the project first.'
                                                        }
                                                        position="bottom"
                                                    >
                                                        <span className={styles.inlineTooltipWrap}>
                                                            <button
                                                                type="button"
                                                                className={formStyles.buttonSecondary}
                                                                disabled
                                                                aria-disabled="true"
                                                            >
                                                                Preview
                                                            </button>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className={formStyles.buttonSecondary}
                                                        onClick={() => previewSurvey(type)}
                                                    >
                                                        Preview
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className={formStyles.buttonSecondary}
                                                    onClick={() => clearMyResponse(type)}
                                                    disabled={clearBusyType === type}
                                                >
                                                    {clearBusyType === type
                                                        ? 'Clearing…'
                                                        : confirmClearType === type
                                                            ? 'Click again to clear'
                                                            : 'Clear my response'}
                                                </button>
                                            </>
                                        ) : null}
                                        {enabled ? (
                                            <button
                                                type="button"
                                                className={formStyles.buttonDanger}
                                                onClick={() => disableSurvey(type)}
                                                disabled={settingsLocked}
                                            >
                                                {confirmDisableType === type
                                                    ? 'Click again to disable'
                                                    : 'Disable'}
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className={formStyles.buttonPrimary}
                                                onClick={() => enableSurvey(type)}
                                                disabled={settingsLocked}
                                            >
                                                Enable
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {enabled ? (
                                    <div
                                        className={[
                                            styles.surveyBody,
                                            settingsLocked ? formStyles.panelDisabled : '',
                                        ].filter(Boolean).join(' ')}
                                    >
                                        <div className={styles.field}>
                                            <label
                                                className={styles.fieldLabel}
                                                htmlFor={`survey-title-${type}`}
                                            >
                                                Title (optional)
                                            </label>
                                            <input
                                                id={`survey-title-${type}`}
                                                className={styles.input}
                                                value={survey.title}
                                                onChange={(e) => patchSurvey(type, {
                                                    title: e.target.value,
                                                })}
                                                placeholder={getSurveyTypeLabel(type)}
                                                disabled={settingsLocked}
                                            />
                                        </div>
                                        <div className={styles.field}>
                                            <label
                                                className={styles.fieldLabel}
                                                htmlFor={`survey-instr-${type}`}
                                            >
                                                Instructions
                                            </label>
                                            <textarea
                                                id={`survey-instr-${type}`}
                                                className={styles.textarea}
                                                value={survey.instructions}
                                                onChange={(e) => patchSurvey(type, {
                                                    instructions: e.target.value,
                                                })}
                                                placeholder="Shown at the top of the survey page"
                                                disabled={settingsLocked}
                                            />
                                        </div>

                                        <div className={styles.questionsHeader}>
                                            <span className={styles.fieldLabel}>
                                                Questions
                                            </span>
                                            <button
                                                type="button"
                                                className={formStyles.buttonSecondary}
                                                onClick={() => addQuestion(type)}
                                                disabled={settingsLocked}
                                            >
                                                Add question
                                            </button>
                                        </div>

                                        {survey.questions.map((q, i) => (
                                            <QuestionConfigSection
                                                key={`${type}-${q.id}-${i}`}
                                                questionIndex={i}
                                                question={q}
                                                onChange={(patch) => updateQuestion(
                                                    type,
                                                    i,
                                                    patch,
                                                )}
                                                onRemove={() => removeQuestion(type, i)}
                                                canRemove={survey.questions.length > 1}
                                                onOpenBulkCategories={(qi) => {
                                                    setBulkQuestionKey(`${type}\0${qi}`);
                                                }}
                                                onMoveUp={() => moveQuestion(type, i, i - 1)}
                                                onMoveDown={() => moveQuestion(type, i, i + 1)}
                                                canMoveUp={i > 0}
                                                canMoveDown={i < survey.questions.length - 1}
                                            />
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>

            {bulkSurveyType != null && bulkQIndex >= 0 ? (
                <BulkImportModal
                    mode="categories"
                    onClose={() => setBulkQuestionKey(null)}
                    onApply={(parsed, mergeMode) => {
                        const survey = surveys.find((s) => s.type === bulkSurveyType);
                        const q = survey?.questions?.[bulkQIndex];
                        if (!q) return;
                        const categories = Array.isArray(q.categories) ? q.categories : [];
                        if (mergeMode === 'replace') {
                            updateQuestion(bulkSurveyType, bulkQIndex, { categories: parsed });
                        } else {
                            const existingIds = new Set(categories.map((c) => String(c.id)));
                            const merged = [...categories];
                            (parsed || []).forEach((p) => {
                                if (!existingIds.has(String(p.id))) {
                                    existingIds.add(String(p.id));
                                    merged.push({ id: p.id, label: p.label || p.id });
                                }
                            });
                            updateQuestion(bulkSurveyType, bulkQIndex, { categories: merged });
                        }
                        setBulkQuestionKey(null);
                    }}
                />
            ) : null}
        </>
    );
}

export default SurveysSettingsPanel;
